(() => {
  if (window.__awCartLoaded) {
    return;
  }

  window.__awCartLoaded = true;

  const selectors = {
    root: "[data-aw-cart]",
    calendarSlot: "[data-aw-cart-calendar]",
    calendar: ".cc-calendar-overall, [class*='cc-calendar-overall']",
    additionalCheckout: ".cart__footer__additional",
    shopPay: "shop-pay-wallet-button",
    googlePay: "shopify-google-pay-button",
  };

  class AWCart {
    constructor(root) {
      this.root = root;
      this.calendarSlot = root.querySelector(selectors.calendarSlot);
      this.observer = null;
      this.syncFrame = null;
      this.isSyncing = false;

      this.mountExternalBlocks = this.mountExternalBlocks.bind(this);
      this.handleMutations = this.handleMutations.bind(this);

      this.init();
    }

    init() {
      this.mountExternalBlocks();

      this.observer = new MutationObserver(this.handleMutations);

      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    handleMutations() {
      if (this.syncFrame) {
        return;
      }

      this.syncFrame = window.requestAnimationFrame(() => {
        this.syncFrame = null;
        this.mountExternalBlocks();
      });
    }

    findCalendarNodes() {
      return Array.from(document.querySelectorAll(selectors.calendar));
    }

    isTemplateCalendar(calendar) {
      return Boolean(calendar.closest("[id^='shopify-block-'], .shopify-block, .shopify-app-block"));
    }

    moveCalendar() {
      if (!this.calendarSlot) {
        return;
      }

      const calendarNodes = this.findCalendarNodes();

      if (!calendarNodes.length) {
        this.root.classList.remove("aw-cart--has-calendar");
        return;
      }

      const slotCalendar = calendarNodes.find(
        (calendar) => this.calendarSlot.contains(calendar) && !this.isTemplateCalendar(calendar),
      );
      const primaryCalendar =
        slotCalendar ||
        calendarNodes.find((calendar) => !this.isTemplateCalendar(calendar)) ||
        calendarNodes.find((calendar) => !calendar.closest(".aw-cart__summary")) ||
        calendarNodes[0];
      const calendarBlock =
        this.isTemplateCalendar(primaryCalendar)
          ? primaryCalendar.closest("[id^='shopify-block-'], .shopify-block, .shopify-app-block") || primaryCalendar
          : primaryCalendar;

      if (!this.calendarSlot.contains(calendarBlock)) {
        this.calendarSlot.appendChild(calendarBlock);
      }

      this.root.querySelectorAll(".aw-cart__summary").forEach((summary) => {
        summary.querySelectorAll(selectors.calendar).forEach((calendar) => {
          calendar.remove();
        });
      });

      this.findCalendarNodes().forEach((calendar) => {
        if (this.calendarSlot.contains(calendar) || calendar === primaryCalendar) {
          return;
        }

        if (this.isTemplateCalendar(calendar)) {
          calendar.style.display = "none";
          return;
        }

        calendar.remove();
      });

      this.root.classList.add("aw-cart--has-calendar");
    }

    normalizeCurrencyLabels() {
      this.root.querySelectorAll("[data-cart-final], [data-cart-subtotal] .cart__footer__value").forEach((node) => {
        if (!node || !node.textContent) {
          return;
        }

        const nextText = node.textContent.replace(/\s*JPY\b/g, "").trim();

        if (nextText && nextText !== node.textContent.trim()) {
          node.textContent = nextText;
        }
      });
    }

    mountExternalBlocks() {
      if (this.isSyncing) {
        return;
      }

      this.isSyncing = true;
      this.moveCalendar();
      this.normalizeCurrencyLabels();
      this.enhanceWalletButtons();
      this.isSyncing = false;
    }

    enhanceWalletButtons() {
      const additionalCheckout = this.root.querySelector(selectors.additionalCheckout);

      if (!additionalCheckout) {
        return;
      }

      [
        { selector: selectors.shopPay, modifier: "shop-pay" },
        { selector: selectors.googlePay, modifier: "google-pay" },
      ].forEach(({ selector, modifier }) => {
        additionalCheckout.querySelectorAll(selector).forEach((button) => {
          if (button.closest(".aw-cart__wallet")) {
            return;
          }

          const wrapper = document.createElement("div");
          const slot = button.getAttribute("slot");
          wrapper.className = `aw-cart__wallet aw-cart__wallet--${modifier}`;

          if (slot) {
            wrapper.setAttribute("slot", slot);
            button.removeAttribute("slot");
          }

          button.parentNode.insertBefore(wrapper, button);
          wrapper.appendChild(button);
        });
      });
    }

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
      }

      if (this.syncFrame) {
        window.cancelAnimationFrame(this.syncFrame);
      }
    }
  }

  const instances = new Map();

  function init(root) {
    if (!root) {
      return;
    }

    const id = root.dataset.sectionId || Math.random().toString(36).slice(2);
    const current = instances.get(id);

    if (current) {
      current.destroy();
    }

    instances.set(id, new AWCart(root));
  }

  function initAll(container = document) {
    container.querySelectorAll(selectors.root).forEach((root) => {
      init(root);
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target.querySelector(selectors.root);

    if (!root) {
      return;
    }

    const id = root.dataset.sectionId;
    const instance = instances.get(id);

    if (instance) {
      instance.destroy();
      instances.delete(id);
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(document));
  } else {
    initAll(document);
  }
})();
